import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Typography, withStyles} from '@material-ui/core'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import {getQuestionKey} from '../reorderUtils'
import ReorderCard from './ReorderCard'
import styles from '../../styles/ReorderList.styles'

const STATUS_TIMEOUT = 2600

const ReorderList = props => {
  const {classes, questions, onMoveQuestion, onEditQuestion} = props

  const [status, setStatus] = useState('')
  const statusTimer = useRef(null)

  useEffect(() => () => clearTimeout(statusTimer.current), [])

  const announce = newIndex => {
    if (newIndex < 0) return

    setStatus(`Moved to position ${newIndex + 1} of ${questions.length}.`)
    clearTimeout(statusTimer.current)
    statusTimer.current = setTimeout(() => setStatus(''), STATUS_TIMEOUT)
  }

  const moveTo = (fromIndex, toIndex) => announce(onMoveQuestion(fromIndex, toIndex))
  const nudge = (index, direction) => moveTo(index, index + direction)

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 4}}),
    useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates}),
  )

  const sortableIds = questions.map(getQuestionKey)

  const handleDragEnd = ({active, over}) => {
    if (!over || active.id === over.id) return

    moveTo(sortableIds.indexOf(active.id), sortableIds.indexOf(over.id))
  }

  return (
    <div>
      <Typography className={classes.reorderHint}>
        Drag a card to change where the question appears, or pick a position from its number. Order
        saves as you go.
      </Typography>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
          <div className={classes.list} role="list">
            {questions.map((question, index) => (
              <ReorderCard
                key={getQuestionKey(question)}
                question={question}
                index={index}
                total={questions.length}
                onNudge={nudge}
                onMoveTo={moveTo}
                onEdit={onEditQuestion}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className={classes.status} role="status" aria-live="polite">
        {status}
      </div>
    </div>
  )
}

ReorderList.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  questions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onMoveQuestion: PropTypes.func.isRequired,
  onEditQuestion: PropTypes.func.isRequired,
}

export default withStyles(styles)(ReorderList)
